import { useContext, useEffect, useReducer, useRef } from 'react'
import { useOverlay } from '@fs/zion-ui'
import { awesomeAjaxReTrier, deepCopy } from '../../../../../../util/Utils'
import useNotifications from '../../../../../../hooks/useNotifications'
import { DatsJobBaseContext } from '../../DatsJobContext'
import { passThrough, postResultQuery } from '../../../../../../services/toolsService'

export default () => {}

/* eslint-disable no-underscore-dangle */

export const constants = {
  // Info taken from org.familysearch.dat.model.ReservedResponseColumn
  // Info taken from org.familysearch.dat.model.AdminToolResponseItem
  BULT_IN_COLUMNS: [
    'id',
    'successful',
    'enqueue_time',
    'dequeue_time',
    'request_time',
    'start_time',
    'end_time',
    'response_time',
    'stored_time',
  ],
  BUILT_IN_COLUMNS: [
    { label: 'Id', value: 'id' },
    { label: 'Successful', value: 'successful' },
    { label: 'Enqueue time', value: 'enqueue_time' },
    { label: 'Dequeue time', value: 'dequeue_time' },
    { label: 'Request time', value: 'request_time' },
    { label: 'Start time', value: 'start_time' },
    { label: 'End time', value: 'end_time' },
    { label: 'Response time', value: 'response_time' },
    { label: 'Stored time', value: 'stored_time' },
  ],
  ALL_COLUMNS: 'ALL_COLUMNS',
  JUST_INCLUDE: 'JUST_INCLUDE',
  EXCLUDE: 'EXCLUDE',
  ALL_ITEMS: 'ALL_ITEMS',
  JUST_FAILED_ITEMS: 'JUST_FAILED_ITEMS',
  JUST_SUCCESSFUL_ITEMS: 'JUST_SUCCESSFUL_ITEMS',
  CONTAINS: 'CONTAINS',
  NO_CONTAINS: 'NO_CONTAINS',
  // Info taken from org.familysearch.dat.inputandresults.results.OutputColumnType
  BUILT_IN_COLUMNS_GROUP: 'BuiltIn',
  INPUT_COLUMNS_GROUP: 'Input',
  OUTPUT_COLUMNS_GROUP: 'ResultsValue',
}

function getNewInitialState() {
  const newState = deepCopy({
    resultQuery: {
      resultsStoreName: '',
      start: 1,
      count: 0,
      requestItemFilter: {
        notNullValues: [],
        nullValues: [],
        valuesThatContain: {}, // key is a column, and value is an array [],
        valuesThatDoNotContain: {}, // key is a column, and value is an array [],
      },
      responseItemFilter: {
        successExcluded: false,
        failureExcluded: false,
        notNullValues: [],
        nullValues: [],
        valuesThatContain: {}, // key is a column, and value is an array [],
        valuesThatDoNotContain: {}, // key is a column, and value is an array [],
      },
      outputColumns: [], // Items look like { name: "", type: ""}
      outputColumnsIsAWhiteList: false,
    },
    formOptions: {
      columnFilterTypes: [
        { value: constants.ALL_COLUMNS, label: 'All Columns' },
        { value: constants.JUST_INCLUDE, label: 'Just Include' },
        { value: constants.EXCLUDE, label: 'Exclude' },
      ],
      itemsFilterRadioButons: [
        { value: constants.ALL_ITEMS, label: 'All Items' },
        { value: constants.JUST_FAILED_ITEMS, label: 'Just Failed Items' },
        { value: constants.JUST_SUCCESSFUL_ITEMS, label: 'Just Successful Items' },
      ],
      rowFilterOperations: [
        { value: constants.CONTAINS, label: 'Contains' },
        { value: constants.NO_CONTAINS, label: "Doesn't Contain" },
      ],
      rowFilterColumnSuggestions: [],
      builtinColumnChips: [],
      inputColumnChips: [],
      outputColumnChips: [],
    },
    formSelections: {
      columnFilterType: constants.ALL_COLUMNS,
      itemsRadioButton: constants.ALL_ITEMS,
      rowFilterSelectColumn: undefined,
      rowFilterOperation: '',
      rowFilterText: '',
      rowFilters: {},
      columnFilters: [],
      createFile: true,
    },
  })

  return newState
}

export function useCreateResultQuery(refreshResultQueries) {
  const { error } = useNotifications()
  const { datsJobState } = useContext(DatsJobBaseContext)

  const reducer = (_, currentState) => deepCopy(currentState)

  const reducerOutput = useReducer(reducer, getNewInitialState())
  let createResultQueryState = reducerOutput[0]

  const setCreateResultQueryState = reducerOutput[1]
  const isMounted = useRef(true)
  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])
  const createResultQueryActions = {
    dialogOverlay: useOverlay(),
    prepareFormOptions: () => {
      if (!datsJobState.jobData || createResultQueryState.formOptions.rowFilterColumnSuggestions.length !== 0) {
        return
      }
      const parameterSpecifications = datsJobState.jobData?.adminTool?.parameterSpecifications || []
      const inputColumns = parameterSpecifications.map((parameter) => {
        return parameter.name
      })
      const outputColumns = datsJobState.jobData?.outputValueNames || []
      // setting up form Options

      constants.BULT_IN_COLUMNS.forEach((builtinColumn) => {
        createResultQueryState.formOptions.rowFilterColumnSuggestions.push({
          key: `${constants.BUILT_IN_COLUMNS_GROUP}${builtinColumn}`,
          primaryText: builtinColumn,
          group: constants.BUILT_IN_COLUMNS_GROUP,
          columnName: builtinColumn,
          columnGroupName: constants.BUILT_IN_COLUMNS_GROUP,
        })
        createResultQueryState.formOptions.builtinColumnChips.push({
          key: `${constants.BUILT_IN_COLUMNS_GROUP}${builtinColumn}`,
          value: builtinColumn,
          label: builtinColumn,
          columnName: builtinColumn,
          columnGroupName: constants.BUILT_IN_COLUMNS_GROUP,
        })
      })

      inputColumns.forEach((inputColumn) => {
        createResultQueryState.formOptions.rowFilterColumnSuggestions.push({
          key: `${constants.INPUT_COLUMNS_GROUP}${inputColumn}`,
          primaryText: inputColumn,
          group: constants.INPUT_COLUMNS_GROUP,
          columnName: inputColumn,
          columnGroupName: constants.INPUT_COLUMNS_GROUP,
        })
        createResultQueryState.formOptions.inputColumnChips.push({
          key: `${constants.INPUT_COLUMNS_GROUP}${inputColumn}`,
          value: inputColumn,
          label: inputColumn,
          columnName: inputColumn,
          columnGroupName: constants.INPUT_COLUMNS_GROUP,
        })
      })

      outputColumns.forEach((outputColumn) => {
        createResultQueryState.formOptions.rowFilterColumnSuggestions.push({
          key: `${constants.OUTPUT_COLUMNS_GROUP}${outputColumn}`,
          primaryText: outputColumn,
          group: constants.OUTPUT_COLUMNS_GROUP,
          columnName: outputColumn,
          columnGroupName: constants.OUTPUT_COLUMNS_GROUP,
        })
        createResultQueryState.formOptions.outputColumnChips.push({
          key: `${constants.OUTPUT_COLUMNS_GROUP}${outputColumn}`,
          value: outputColumn,
          label: outputColumn,
          columnName: outputColumn,
          columnGroupName: constants.OUTPUT_COLUMNS_GROUP,
        })
      })
    },
    openDialog: () => {
      createResultQueryActions.prepareFormOptions()
      createResultQueryActions.dialogOverlay.open()
    },
    setColumnFilterType: (columnFilterType) => {
      createResultQueryState.formSelections.columnFilterType = columnFilterType
      createResultQueryActions.updateCreateResultsQuery()
    },
    setItemsRadioButton: (selectedRadioButton) => {
      createResultQueryState.formSelections.itemsRadioButton = selectedRadioButton
      createResultQueryActions.updateCreateResultsQuery()
    },
    setRowFilterSelectColumn: (newSuggestion) => {
      createResultQueryState.formSelections.rowFilterSelectColumn = newSuggestion
      createResultQueryActions.updateCreateResultsQuery()
    },
    setRowFilterOperation: (rowFilterOperation) => {
      createResultQueryState.formSelections.rowFilterOperation = rowFilterOperation
      createResultQueryActions.updateCreateResultsQuery()
    },
    setRowFilterText: (rowFilterText) => {
      createResultQueryState.formSelections.rowFilterText = rowFilterText
      createResultQueryActions.updateCreateResultsQuery()
    },
    addRowFilter: () => {
      const columnGroupName = deepCopy(createResultQueryState.formSelections.rowFilterSelectColumn.columnGroupName)
      const columnName = deepCopy(createResultQueryState.formSelections.rowFilterSelectColumn.columnName)
      const operation = deepCopy(createResultQueryState.formSelections.rowFilterOperation)
      const value = deepCopy(createResultQueryState.formSelections.rowFilterText)
      const rowId = `${columnGroupName}${columnName}${operation}${value}`

      createResultQueryState.formSelections.rowFilters[rowId] = {
        rowId,
        columnGroupName,
        columnName,
        operation,
        value,
      }
      createResultQueryActions.resetRowFilter()
    },
    setCreateFile: (createFile) => {
      createResultQueryState.formSelections.createFile = createFile
      createResultQueryActions.updateCreateResultsQuery()
    },
    toggleCreateFile: () => {
      createResultQueryActions.setCreateFile(!createResultQueryState.formSelections.createFile)
    },
    addJustIncludeColumn: (chip) => {
      createResultQueryState.formSelections.columnFilters.push(chip)
      createResultQueryActions.updateCreateResultsQuery()
    },
    delJustIncludeColumn: (chip) => {
      for (let i = 0; i < createResultQueryState.formSelections.columnFilters.length; i++) {
        if (createResultQueryState.formSelections.columnFilters[i].key === chip.key) {
          createResultQueryState.formSelections.columnFilters.splice(i, 1)
        }
      }
      createResultQueryActions.updateCreateResultsQuery()
    },
    deleteRowFilter: (rowId) => {
      delete createResultQueryState.formSelections.rowFilters[rowId]
      createResultQueryActions.updateCreateResultsQuery()
    },
    resetRowFilter: () => {
      createResultQueryState.formSelections.rowFilterSelectColumn = undefined
      createResultQueryState.formSelections.rowFilterOperation = ''
      createResultQueryState.formSelections.rowFilterText = ''
      createResultQueryActions.updateCreateResultsQuery()
    },
    reset: () => {
      createResultQueryState = getNewInitialState()
      createResultQueryActions.updateCreateResultsQuery()
    },
    close: () => {
      refreshResultQueries()
      createResultQueryActions.reset()
      createResultQueryActions.dialogOverlay.close()
    },
    makeResultQuery: () => {
      // Columns
      if (createResultQueryState.formSelections.columnFilterType === constants.ALL_COLUMNS) {
        createResultQueryState.resultQuery.outputColumns = []
        createResultQueryState.resultQuery.outputColumnsIsAWhiteList = false
      } else if (createResultQueryState.formSelections.columnFilterType === constants.JUST_INCLUDE) {
        createResultQueryState.formSelections.columnFilters.forEach((columnFilter) => {
          createResultQueryState.resultQuery.outputColumns.push({
            name: columnFilter.columnName,
            type: columnFilter.columnGroupName,
          })
        })
        createResultQueryState.resultQuery.outputColumnsIsAWhiteList = true
      } else if (createResultQueryState.formSelections.columnFilterType === constants.EXCLUDE) {
        createResultQueryState.formSelections.columnFilters.forEach((columnFilter) => {
          createResultQueryState.resultQuery.outputColumns.push({
            name: columnFilter.columnName,
            type: columnFilter.columnGroupName,
          })
        })
        createResultQueryState.resultQuery.outputColumnsIsAWhiteList = false
      }

      // Just failed or successful items
      if (createResultQueryState.formSelections.itemsRadioButton === constants.ALL_ITEMS) {
        createResultQueryState.resultQuery.responseItemFilter.successExcluded = false
        createResultQueryState.resultQuery.responseItemFilter.failureExcluded = false
      } else if (createResultQueryState.formSelections.itemsRadioButton === constants.JUST_FAILED_ITEMS) {
        createResultQueryState.resultQuery.responseItemFilter.successExcluded = true
        createResultQueryState.resultQuery.responseItemFilter.failureExcluded = false
      } else if (createResultQueryState.formSelections.itemsRadioButton === constants.JUST_SUCCESSFUL_ITEMS) {
        createResultQueryState.resultQuery.responseItemFilter.successExcluded = false
        createResultQueryState.resultQuery.responseItemFilter.failureExcluded = true
      }

      // Rows
      Object.keys(createResultQueryState.formSelections.rowFilters).forEach((rowFilterKey) => {
        const rowFilter = createResultQueryState.formSelections.rowFilters[rowFilterKey]
        if (rowFilter.columnGroupName === constants.INPUT_COLUMNS_GROUP) {
          if (rowFilter.operation === constants.CONTAINS) {
            createResultQueryState.resultQuery.requestItemFilter.valuesThatContain[rowFilter.columnName] =
              createResultQueryState.resultQuery.requestItemFilter.valuesThatContain[rowFilter.columnName] || []
            createResultQueryState.resultQuery.requestItemFilter.valuesThatContain[rowFilter.columnName].push(
              rowFilter.value
            )
          } else if (rowFilter.operation === constants.NO_CONTAINS) {
            createResultQueryState.resultQuery.requestItemFilter.valuesThatDoNotContain[rowFilter.columnName] =
              createResultQueryState.resultQuery.requestItemFilter.valuesThatDoNotContain[rowFilter.columnName] || []
            createResultQueryState.resultQuery.requestItemFilter.valuesThatDoNotContain[rowFilter.columnName].push(
              rowFilter.value
            )
          }
        } else if (
          rowFilter.columnGroupName === constants.OUTPUT_COLUMNS_GROUP ||
          rowFilter.columnGroupName === constants.BUILT_IN_COLUMNS_GROUP
        ) {
          if (rowFilter.operation === constants.CONTAINS) {
            createResultQueryState.resultQuery.responseItemFilter.valuesThatContain[rowFilter.columnName] =
              createResultQueryState.resultQuery.responseItemFilter.valuesThatContain[rowFilter.columnName] || []
            createResultQueryState.resultQuery.responseItemFilter.valuesThatContain[rowFilter.columnName].push(
              rowFilter.value
            )
          } else if (rowFilter.operation === constants.NO_CONTAINS) {
            createResultQueryState.resultQuery.responseItemFilter.valuesThatDoNotContain[rowFilter.columnName] =
              createResultQueryState.resultQuery.responseItemFilter.valuesThatDoNotContain[rowFilter.columnName] || []
            createResultQueryState.resultQuery.responseItemFilter.valuesThatDoNotContain[rowFilter.columnName].push(
              rowFilter.value
            )
          }
        }
      })

      createResultQueryActions.updateCreateResultsQuery() // This updates the state in the tests

      postResultQuery(datsJobState.jobId, createResultQueryState.resultQuery)
        .then((data) => {
          if (createResultQueryState.formSelections.createFile) {
            const functiontoRetry = () => {
              return passThrough(data._links.generate.href, 'PUT')
            }
            awesomeAjaxReTrier(functiontoRetry, 404)
              .then(() => {
                createResultQueryActions.close()
              })
              .catch((err) => {
                const queryId = data._links.self.href.split('/')[4]
                error(`Error generating file for result query: ${queryId}`, err)
              })
          } else {
            createResultQueryActions.close()
          }
        })
        .catch((err) => {
          error(`Error making result query for Job ID: ${datsJobState.jobId}`, err)
        })
    },
    updateCreateResultsQuery: () => {
      if (isMounted.current) setCreateResultQueryState(createResultQueryState)
    },
  }

  return { createResultQueryState, createResultQueryActions }
}

/* eslint-enable no-underscore-dangle */
